import { useStore } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import { createStore } from 'zustand/vanilla'

interface Car {
  driverId: string | null
  driverBirthDate: Date
  driverHasCreditCard: boolean
  bookedEquipmentsWithQuantity: {
    id: string
    quantity: number
  }[]
  isEditing: boolean
}

interface FilterableCarsPagination {
  currentPage: number
  totalPages: number
  isFirstPage: boolean
  isLastPage: boolean
}

interface CarRentalFilteringServiceState {
  cars: Record<string, Car>
  editingCarsList: string[]
  isCarBeingSelected: boolean
  isCarBeingUpgraded: boolean
  isShowingExtraCars: boolean
  isShowingFilterableCars: boolean
  filterableCarsPagination: FilterableCarsPagination
  upgradedCar: string
  selectedCar: string
  isFetchingCars: boolean
}

interface CarRentalFilteringServiceStoreActions {
  startEditingCar: (carId: string, carState: Car) => void
  endEditingCar: (carId: string) => void
  endEditingCarsExcept: (carIds: string[]) => void
  setDriverId: (carId: string, driverId: string | null) => void
  setDriverBirthDate: (carId: string, birthDate: Date) => void
  setDriverHasCreditCard: (carId: string, hasCreditCard: boolean) => void
  toggleEquipmentEnquiry: (carId: string, equipmentId: string, quantity: number) => void
  setCarIsBeingUpgraded: (upgradingCar: boolean) => void
  setShowExtraCars: (showExtraCars: boolean) => void
  setShowFilterableCars: (showFilterableCars: boolean) => void
  setCarToBeUpgraded: (carId: string) => void
  setCarToBeSelected: (carId: string) => void
  goToNextPage: () => void
  goToPreviousPage: () => void
  goToPage: (page: number) => void
  setTotalPages: (totalPages: number) => void
  setIsFetchingCars: (isFetching: boolean) => void
  setIsSelectingCar: (isSelecting: boolean) => void
}

type CarRentalFilteringServiceStore = CarRentalFilteringServiceState & CarRentalFilteringServiceStoreActions

function updatePagination(state: CarRentalFilteringServiceState, page: number) {
  state.filterableCarsPagination.currentPage = page
  state.filterableCarsPagination.isFirstPage = page === 1
  state.filterableCarsPagination.isLastPage = page === state.filterableCarsPagination.totalPages
}

export function createCarRentalFilteringServiceStore(initState: CarRentalFilteringServiceState) {
  return createStore<CarRentalFilteringServiceStore>()(
    immer(set => ({
      ...initState,
      setCarIsBeingUpgraded(upgradingCar) {
        set((state) => {
          state.isCarBeingUpgraded = upgradingCar
        })
      },
      setCarToBeUpgraded(carId) {
        set((state) => {
          state.upgradedCar = carId
        })
      },
      setCarToBeSelected(carId) {
        set((state) => {
          state.selectedCar = carId
        })
      },
      setShowExtraCars(showExtraCars) {
        set((state) => {
          state.isShowingExtraCars = showExtraCars
        })
      },
      setShowFilterableCars(showFilterableCars) {
        set((state) => {
          state.isShowingFilterableCars = showFilterableCars
        })
      },
      startEditingCar(carId, carState) {
        set(({ cars, editingCarsList }) => {
          cars[carId] = carState
          editingCarsList.push(carId)
        })
      },
      endEditingCar(carId) {
        set((state) => {
          state.editingCarsList = state.editingCarsList.filter(id => id !== carId)
        })
      },
      endEditingCarsExcept(carIds) {
        set((state) => {
          state.editingCarsList = state.editingCarsList.filter(id => carIds.includes(id))
        })
      },
      setDriverId(carId, driverId) {
        set(({ cars }) => {
          if (!cars[carId])
            return
          cars[carId].driverId = driverId
        })
      },
      setDriverBirthDate(carId, birthDate) {
        set(({ cars }) => {
          if (!cars[carId])
            return
          cars[carId].driverBirthDate = birthDate
        })
      },
      setDriverHasCreditCard(carId, hasCreditCard) {
        set(({ cars }) => {
          if (!cars[carId])
            return
          cars[carId].driverHasCreditCard = hasCreditCard
        })
      },
      toggleEquipmentEnquiry(carId, equipmentId, quantity) {
        set(({ cars }) => {
          if (!cars[carId])
            return
          const bookedEquipments = cars[carId].bookedEquipmentsWithQuantity

          const equipmentIndex = bookedEquipments.findIndex(eq => eq.id === equipmentId)

          if (equipmentIndex !== -1) {
            bookedEquipments[equipmentIndex].quantity = quantity
            if (quantity <= 0) {
              bookedEquipments.splice(equipmentIndex, 1)
            }
          }
          else {
            bookedEquipments.push({ id: equipmentId, quantity })
          }
        })
      },
      goToNextPage() {
        set((state) => {
          const { currentPage, totalPages } = state.filterableCarsPagination
          if (currentPage < totalPages) {
            updatePagination(state, currentPage + 1)
          }
        })
      },
      goToPreviousPage() {
        set((state) => {
          const { currentPage } = state.filterableCarsPagination
          if (currentPage > 1) {
            updatePagination(state, currentPage - 1)
          }
        })
      },
      goToPage(page) {
        set((state) => {
          const { totalPages } = state.filterableCarsPagination
          if (page < 1 || page > totalPages)
            return
          updatePagination(state, page)
        })
      },
      setTotalPages(totalPages) {
        set((state) => {
          state.filterableCarsPagination.totalPages = totalPages
          updatePagination(state, 1)
        })
      },
      setIsFetchingCars(isFetching) {
        set((state) => {
          state.isFetchingCars = isFetching
        })
      },
      setIsSelectingCar(isSelecting) {
        set((state) => {
          state.isCarBeingSelected = isSelecting
        })
      },
    })),
  )
}

export function editingCar(carId: string) {
  return ({ cars }: CarRentalFilteringServiceState) => {
    return cars[carId]
  }
}

export const carSelectionLoading = ({ isCarBeingSelected }: CarRentalFilteringServiceState) => isCarBeingSelected

export const carIsBeingUpgraded = ({ isCarBeingUpgraded }: CarRentalFilteringServiceState) => isCarBeingUpgraded

export const carToBeUpgraded = ({ upgradedCar }: CarRentalFilteringServiceState) => upgradedCar

export const carIsBeingSelected = ({ selectedCar }: CarRentalFilteringServiceState) => Boolean(selectedCar)

export const carToBeSelected = ({ selectedCar }: CarRentalFilteringServiceState) => selectedCar

export const showingExtraCars = ({ isShowingExtraCars }: CarRentalFilteringServiceState) => isShowingExtraCars

export function showingFilterableCars({ isShowingFilterableCars }: CarRentalFilteringServiceState) {
  return isShowingFilterableCars
}
export function carCouldHaveHigherPrice(carId: string, maxAge: Date, minAge: Date) {
  return ({ cars }: CarRentalFilteringServiceState) => {
    const birthdate = cars[carId]?.driverBirthDate
    if (!birthdate)
      return false
    return !(birthdate >= maxAge && birthdate <= minAge)
  }
}

export const filtersAreBeingApplied = ({ isFetchingCars }: CarRentalFilteringServiceState) => isFetchingCars

export const carRentalFilteringServiceStore = createCarRentalFilteringServiceStore({
  cars: {},
  editingCarsList: [],
  isCarBeingSelected: false,
  isCarBeingUpgraded: false,
  isShowingExtraCars: false,
  isShowingFilterableCars: false,
  upgradedCar: '',
  selectedCar: '',
  filterableCarsPagination: {
    currentPage: 1,
    totalPages: 1,
    isFirstPage: true,
    isLastPage: true,
  },
  isFetchingCars: false,
})

type StoreSelector<T, S> = (state: S) => T
interface UseBoundStore<S> {
  (): S
  <T>(selector: StoreSelector<T, S>): T
}

export const useCarRentalFilteringServiceStore: UseBoundStore<CarRentalFilteringServiceStore> = <T>(
  selector?: StoreSelector<T, CarRentalFilteringServiceStore>,
) => {
  const store = useStore(carRentalFilteringServiceStore)

  if (!selector) {
    return store
  }

  return selector(store)
}
